import React, {
  createContext, useContext, useState, useEffect, useCallback, useRef,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { drainQueue } from '@/lib/sync/engine';
import { subscribeWorkOrders, subscribeMolds, subscribeSpareParts, subscribeFailureCodes, subscribeActionCodes, subscribeNotifications, unsubscribeChannel } from '@/lib/sync/realtime';
import type { RealtimeChannel } from '@supabase/supabase-js';
import type { DrainResult } from '@/lib/sync/engine';

// ─── Context shape ────────────────────────────────────────────────────────────

export interface SyncContextValue {
  isSyncing: boolean;
  lastSyncedAt: string | null;
  triggerSync: () => void;
}

export const SyncContext = createContext<SyncContextValue | null>(null);

export function useSync() {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSync must be used within SyncProvider');
  return ctx;
}

// ─── Alert sound (Web Audio API, no external files needed) ───────────────────

function playCriticalAlert(_type: string, _title: string) {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(660, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
    osc.onended = () => ctx.close();
  } catch {
    // AudioContext blocked before user interaction — silent fail
  }
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function SyncProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);

  const channelsRef = useRef<RealtimeChannel[]>([]);
  const orgId = user?.organization_id ?? null;
  const userId = user?.id ?? '';

  // ── Realtime subscriptions ─────────────────────────────────────────────────

  const subscribe = useCallback(() => {
    if (!orgId) return;
    // Clean up stale channels first
    channelsRef.current.forEach(ch => unsubscribeChannel(ch));
    channelsRef.current = [
      subscribeWorkOrders(orgId, queryClient),
      subscribeMolds(orgId, queryClient),
      subscribeSpareParts(orgId, queryClient),
      subscribeFailureCodes(orgId, queryClient),
      subscribeActionCodes(orgId, queryClient),
      ...(userId ? [subscribeNotifications(userId, queryClient, playCriticalAlert)] : []),
    ];
  }, [orgId, userId, queryClient]);

  const unsubscribeAll = useCallback(() => {
    channelsRef.current.forEach(ch => unsubscribeChannel(ch));
    channelsRef.current = [];
  }, []);

  // ── Drain queue ────────────────────────────────────────────────────────────

  const drain = useCallback(async () => {
    if (!orgId || !userId || isSyncing) return;

    setIsSyncing(true);
    let result: DrainResult | null = null;
    try {
      result = await drainQueue(orgId, userId, queryClient);
      setLastSyncedAt(new Date().toISOString());
    } finally {
      setIsSyncing(false);
    }

    // Show conflict notifications
    if (result && result.conflicts.length > 0) {
      result.conflicts.forEach(conflict => {
        toast.warning(
          `WO ${conflict.wo_id} was modified by another user while offline. Your changes were saved — please review.`,
          {
            duration: 10_000,
            action: {
              label: 'View',
              onClick: () => navigate(`/maintenance/work-orders/${conflict.entity_id}`),
            },
          },
        );
      });
    }

    if (result && result.failed > 0) {
      toast.error(`${result.failed} change${result.failed > 1 ? 's' : ''} failed to sync. Tap retry in the sidebar.`, {
        duration: 8_000,
      });
    }
  }, [orgId, userId, isSyncing, queryClient, navigate]);

  const triggerSync = useCallback(() => {
    drain();
  }, [drain]);

  // ── Online / offline event handling ────────────────────────────────────────

  useEffect(() => {
    if (!user) return;

    const handleOnline = () => {
      subscribe();
      drain();
    };

    const handleOffline = () => {
      unsubscribeAll();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // On mount — if already online, subscribe + drain pending ops
    if (navigator.onLine) {
      subscribe();
      drain();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsubscribeAll();
    };
  // drain and subscribe change identity only when orgId/userId change — that's fine
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return (
    <SyncContext.Provider value={{ isSyncing, lastSyncedAt, triggerSync }}>
      {children}
    </SyncContext.Provider>
  );
}
